import React from "react";
import CatalogImage from "../common/CatalogImage";
import Table from "../common/Table";
import { formatCurrency, packageNetPrice, packageRsp } from "../../lib/catalog";
import {
  displayValue,
  galleryFileName,
  groupHotelsByCity,
  hasFlightData,
  hasValue,
  listItems,
  occupancyRates,
  packageFacts,
} from "./packageDetailModel";

function EmptyPanel({ message }) {
  return <div className="agent-package-empty">{message}</div>;
}
function normalizeFlightLocation(value) {
  const raw = String(value || "").trim();

  if (!raw) return "—";

  const saudiCities = [
    "Dzhorat",
    "dzhorat",
  ];

  if (saudiCities.includes(raw.toLowerCase())) {
    return "Bangalore";
  }

  return raw;
}
function FactGrid({ items, className = "" }) {
  return (
    <div className={`agent-package-facts ${className}`.trim()}>
      {items.map((item) => (
        <div className="agent-package-fact" key={item.label}>
          <div className="agent-package-fact-label">{item.label}</div>
          <div className="agent-package-fact-value">{displayValue(item.value, "—")}</div>
        </div>
      ))}
    </div>
  );
}

export function OccupancyRates({ pkg }) {
  return (
    <div className="agent-package-occupancy">
      {occupancyRates(pkg).map((rate) => (
        <div className="agent-package-occupancy-item" key={rate.label}>
          <div className="agent-package-occupancy-heading">
            <span className="agent-package-occupancy-icon" aria-hidden="true">
              <iconify-icon icon={rate.icon}></iconify-icon>
            </span>
            <div className="agent-package-fact-label mb-0">{rate.label}</div>
          </div>
          <div className="agent-package-occupancy-prices">
            <div className="agent-package-occupancy-price">
              <span>Agent Net</span>
              <strong>{formatCurrency(rate.net)}</strong>
            </div>
            <div className="agent-package-occupancy-price agent-package-occupancy-price--rsp">
              <span>RSP</span>
              <strong>{formatCurrency(rate.rsp)}</strong>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FeatureList({ items, variant }) {
  const included = variant === "inclusions";
  return (
    <ul className={`agent-package-feature-list agent-package-feature-list--${variant}`}>
      {items.map((item, index) => (
        <li key={`${variant}-${index}`}>
          <iconify-icon
            icon={included ? "solar:check-circle-bold" : "solar:close-circle-bold"}
            className="agent-package-feature-icon"
            aria-hidden="true" style={{ color: included ? "#198754" : "#dc3545" }}
          ></iconify-icon>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function FeaturePanel({ id, title, emptyMessage, items, variant }) {
  return (
    <div id={`package-tab-panel-${id}`} role="tabpanel" aria-labelledby={`package-tab-${id}`}>
      <h5 className="agent-package-section-title">{title}</h5>
      {items.length ? (
        <FeatureList items={items} variant={variant} />
      ) : (
        <EmptyPanel message={emptyMessage} />
      )}
    </div>
  );
}

export function OverviewPanel({ pkg }) {
  return (
    <div id="package-tab-panel-overview" role="tabpanel" aria-labelledby="package-tab-overview">
      <h5 className="agent-package-section-title">Package summary</h5>
      <FactGrid items={packageFacts(pkg)} />

      <h5 className="agent-package-section-title mt-4">Net occupancy rates</h5>
      <OccupancyRates pkg={pkg} />
      <div className="small text-secondary mt-3">Bookings are charged at Agent Net. RSP is a recommended selling price only and does not affect totals, outstanding, or wallet deduction.</div>
    </div>
  );
}

export function InclusionsPanel({ pkg }) {
  return (
    <FeaturePanel
      id="inclusions"
      title="Inclusions"
      emptyMessage="No inclusions are available for this package."
      items={listItems(pkg.inclusions || pkg.inclusion)}
      variant="inclusions"
    />
  );
}

export function ExclusionsPanel({ pkg }) {
  return (
    <FeaturePanel
      id="exclusions"
      title="Exclusions"
      emptyMessage="No exclusions are available for this package."
      items={listItems(pkg.exclusions || pkg.exclusion)}
      variant="exclusions"
    />
  );
}

export function FlightPanel({ summary, legs }) {
  if (!hasFlightData(summary, legs)) {
    return (
      <div id="package-tab-panel-flight" role="tabpanel" aria-labelledby="package-tab-flight">
        <EmptyPanel message="No flight details are available for this package." />
      </div>
    );
  }

  const summaryRows = [
    { label: "Status", value: summary.status },
    { label: "Adult price", value: summary.adultPrice != null ? formatCurrency(summary.adultPrice) : "" },
    { label: "Seats", value: summary.seats },
    { label: "PNR", value: summary.pnr },
    { label: "Flight block", value: summary.name || summary.code },
  ].filter((item) => hasValue(item.value));

  const onwardLegs = legs.filter((leg) => leg.leg !== "return");
  const returnLegs = legs.filter((leg) => leg.leg === "return");
 
  function FlightTable({ title, subtitle, rows }) {
    if (!rows.length) return null;
    return (
      <div className="mb-4 mt-3">
        <h5 className="agent-package-section-title mb-1">{title}</h5>
        {/* {subtitle ? <div className="small text-secondary mb-3">{subtitle}</div> : null} */}
        <Table>
          <thead>
            <tr>
              <th>Airline</th>
              <th>Flight number</th>
              <th>From</th>
              <th>To</th>
              <th>Departure</th>
              <th>Arrival</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((leg) => (
              <tr key={leg.id}>
                <td>{displayValue(leg.airline, "—")}</td>
                <td>{displayValue(leg.code, "—")}</td>
                {/* <td>{displayValue(leg.departure, "—")}</td>
                <td>{displayValue(leg.arrival, "—")}</td> */}
                <td>{normalizeFlightLocation(leg.departure)}</td>
<td>{normalizeFlightLocation(leg.arrival)}</td>
                <td>{displayValue(leg.departureAt, "—")}</td>
                <td>{displayValue(leg.arrivalAt, "—")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div id="package-tab-panel-flight" role="tabpanel" aria-labelledby="package-tab-flight">
      {summaryRows.length ? (
        <div className="mb-4">
          <h5 className="agent-package-section-title">Flight summary</h5>
          <Table>
            <thead>
              <tr>
                <th>Detail</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}

      <FlightTable title="Onward flight" subtitle="Departure from origin to Saudi" rows={onwardLegs} />
      <FlightTable title="Return flight" subtitle="Return from Saudi to origin" rows={returnLegs} />
    </div>
  );
}

export function HotelsPanel({ hotels }) {
  if (!hotels.length) {
    return (
      <div id="package-tab-panel-hotels" role="tabpanel" aria-labelledby="package-tab-hotels">
        <EmptyPanel message="No hotel details are available for this package." />
      </div>
    );
  }

  const groups = groupHotelsByCity(hotels);

  return (
    <div id="package-tab-panel-hotels" role="tabpanel" aria-labelledby="package-tab-hotels">
      <div className="agent-package-hotels">
        {groups.map((group) => (
          <section key={group.city || group.hotels[0]?.id}>
            {group.city ? <h6 className="agent-package-city-title">{group.city}</h6> : null}
            <div className="agent-package-hotel-list">
              {group.hotels.map((hotel) => (
                <article className="agent-package-hotel" key={hotel.id}>
                  {hotel.image ? (
                    <CatalogImage folder="umrah_hotel_list" fileName={hotel.image} alt={hotel.name || group.city || "Hotel"} height={120} className="rounded" />
                  ) : null}
                  <div className="agent-package-hotel__body">
                    {hotel.name ? <div className="fw-semibold">{hotel.name}</div> : null}
                    <div className="agent-package-hotel__meta">
                      {hotel.city ? <span>{hotel.city}</span> : null}
                      {hotel.checkIn ? <span>Check-in {hotel.checkIn}</span> : null}
                      {hotel.checkOut ? <span>Check-out {hotel.checkOut}</span> : null}
                      {hasValue(hotel.nights) ? <span>{hotel.nights} nights</span> : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function ItineraryPanel({ itinerary }) {
  if (!itinerary.length) {
    return (
      <div id="package-tab-panel-itinerary" role="tabpanel" aria-labelledby="package-tab-itinerary">
        <EmptyPanel message="No itinerary is available for this package." />
      </div>
    );
  }

  return (
    <div id="package-tab-panel-itinerary" role="tabpanel" aria-labelledby="package-tab-itinerary">
      <ol className="agent-package-itinerary">
        {itinerary.map((item) => (
          <li className="agent-package-itinerary-item" key={item.id}>
            <span className="agent-package-itinerary-dot" aria-hidden="true"></span>
            <div className="agent-package-itinerary-body">
              <div className="agent-package-itinerary-day">Day {item.day}</div>
              {item.city ? <div className="agent-package-itinerary-city">{item.city}</div> : null}
              {item.title ? <div className="fw-semibold">{item.title}</div> : null}
              {item.description ? (
                <div className="small mt-1" dangerouslySetInnerHTML={{ __html: item.description }} />
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function GalleryPanel({ gallery, packageName }) {
  const images = gallery.map(galleryFileName).filter(Boolean);

  if (!images.length) {
    return (
      <div id="package-tab-panel-gallery" role="tabpanel" aria-labelledby="package-tab-gallery">
        <EmptyPanel message="No gallery images are available for this package." />
      </div>
    );
  }

  return (
    <div id="package-tab-panel-gallery" role="tabpanel" aria-labelledby="package-tab-gallery">
      <div className="agent-package-gallery">
        {images.map((fileName, index) => (
          <CatalogImage
            key={`${fileName}-${index}`}
            folder="umrah_package_gallery"
            fileName={fileName}
            alt={packageName || "Gallery"}
            height={168}
            className="rounded"
          />
        ))}
      </div>
    </div>
  );
}

export function PricingPanel({ pkg }) {
  return (
    <div id="package-tab-panel-pricing" role="tabpanel" aria-labelledby="package-tab-pricing">
      <h5 className="agent-package-section-title">Package price</h5>
      <FactGrid
        items={[
          { label: "Net price", value: formatCurrency(packageNetPrice(pkg)) },
          { label: "RSP", value: formatCurrency(packageRsp(pkg)) },
        ]}
        className="agent-package-facts--pricing"
      />

      <h5 className="agent-package-section-title mt-4">Occupancy rates</h5>
      <OccupancyRates pkg={pkg} />
      <div className="small text-secondary mt-3">Bookings are charged at Agent Net. RSP is a recommended selling price only and does not affect totals, outstanding, or wallet deduction.</div>
    </div>
  );
}
